import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { STATUS_LABELS, absoluteTime, relativeTime } from './format';

// Small presentational primitives shared across the scheduler pages.
// Status vocabulary is fixed: Queued / Running / Succeeded / Failed /
// Cancelled / Paused — rendered exclusively through StatusBadge.

export function StatusBadge({ status, paused }) {
  if (paused) return <span className="sched-badge sched-badge--paused">Paused</span>;
  if (!status) return <span className="sched-badge sched-badge--none">No runs yet</span>;
  const label = STATUS_LABELS[status] || status;
  return <span className={`sched-badge sched-badge--${status}`}>{label}</span>;
}

export function RelTime({ iso, prefix = '', className = 'sched-badge-time' }) {
  if (!iso) return null;
  return (
    <span className={className} title={absoluteTime(iso)}>
      {prefix}{relativeTime(iso)}
    </span>
  );
}

export function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className="sched-toggle"
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
}

export function Sparkline({ runs }) {
  if (!runs || !runs.length) return null;
  return (
    <span className="sched-spark" title="Recent runs (oldest to newest)">
      {runs.map((r) => {
        const cls = r.status === 'succeeded' ? 'ok'
          : r.status === 'failed' ? 'err'
          : (r.status === 'pending' || r.status === 'running') ? 'open' : '';
        return <i key={r.id} className={cls} title={`${STATUS_LABELS[r.status] || r.status} — ${absoluteTime(r.enqueued_at)}`} />;
      })}
    </span>
  );
}

// The dropdown is portaled to document.body with fixed positioning so it is
// never clipped by overflow containers (e.g. the scrollable schedules table).
export function OverflowMenu({ items }) {
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const place = () => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const estHeight = listRef.current?.offsetHeight || (items.length * 34 + 10);
    const flipUp = rect.bottom + 4 + estHeight > window.innerHeight;
    setPos({
      right: window.innerWidth - rect.right,
      top: flipUp ? undefined : rect.bottom + 4,
      bottom: flipUp ? window.innerHeight - rect.top + 4 : undefined,
    });
  };

  useLayoutEffect(() => {
    if (open) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const closeNow = () => setOpen(false);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', close);
    window.addEventListener('scroll', closeNow, true);
    window.addEventListener('resize', closeNow);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', close);
      window.removeEventListener('scroll', closeNow, true);
      window.removeEventListener('resize', closeNow);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`sched-menu${open ? ' sched-menu--open' : ''}`}>
      <button
        type="button"
        ref={triggerRef}
        className="sched-menu-trigger"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {open && pos && createPortal(
        <div
          className="sched-menu-list"
          role="menu"
          ref={listRef}
          style={{ position: 'fixed', right: pos.right, top: pos.top, bottom: pos.bottom }}
        >
          {items.map(({ label, onClick, danger }) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              className={danger ? 'danger' : ''}
              onClick={() => {
                setOpen(false);
                onClick();
              }}
            >
              {label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

export function ConfirmModal({ title, body, confirmLabel = 'Confirm', danger, onConfirm, onCancel, busy }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="sched-modal-backdrop" onClick={onCancel} role="presentation">
      <div className="sched-modal" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p>{body}</p>
        <div className="sched-modal-actions">
          <button type="button" className="sched-btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className={`sched-btn ${danger ? 'sched-btn--danger' : 'sched-btn--primary'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Skeleton({ height = 80, style }) {
  return <div className="sched-skel" style={{ height, ...style }} />;
}

export function Toast({ message, error }) {
  if (!message) return null;
  return <div className={`sched-toast${error ? ' sched-toast--error' : ''}`}>{message}</div>;
}

export function EmptyState({ title, body, action }) {
  return (
    <div className="sched-empty">
      <h2>{title}</h2>
      <p>{body}</p>
      {action}
    </div>
  );
}
